export class AudioManager {
  private audioContext: AudioContext | null = null
  private masterGain: GainNode | null = null
  private musicOscillator: OscillatorNode | null = null
  private musicGain: GainNode | null = null
  private bassOscillator: OscillatorNode | null = null
  private bassGain: GainNode | null = null
  private isPlaying = false
  private tempo = 140
  private currentBeat = 0
  private beatInterval: number | null = null

  initialize() {
    this.audioContext = new (window.AudioContext ||
      (window as any).webkitAudioContext)()
    this.masterGain = this.audioContext.createGain()
    this.masterGain.connect(this.audioContext.destination)
    this.masterGain.gain.value = 0.3
  }

  startMusic(intensity: number = 0.5) {
    if (!this.audioContext || this.isPlaying) return

    this.isPlaying = true

    // Lead melody
    this.musicOscillator = this.audioContext.createOscillator()
    this.musicGain = this.audioContext.createGain()
    this.musicOscillator.connect(this.musicGain)
    this.musicGain.connect(this.masterGain!)
    this.musicOscillator.type = 'square'
    this.musicGain.gain.value = 0.15 * intensity
    this.musicOscillator.start()

    // Bass line
    this.bassOscillator = this.audioContext.createOscillator()
    this.bassGain = this.audioContext.createGain()
    this.bassOscillator.connect(this.bassGain)
    this.bassGain.connect(this.masterGain!)
    this.bassOscillator.type = 'sawtooth'
    this.bassGain.gain.value = 0.1 * intensity
    this.bassOscillator.start()

    this.playMelody()
  }

  private playMelody() {
    const melodyNotes = [262, 294, 330, 349, 392, 440, 494, 523] // C major scale
    const bassNotes = [131, 147, 165, 175] // Bass notes

    this.beatInterval = window.setInterval(
      () => {
        if (!this.musicOscillator || !this.bassOscillator) return

        const noteIndex = this.currentBeat % melodyNotes.length
        const bassIndex = Math.floor(this.currentBeat / 2) % bassNotes.length

        this.musicOscillator.frequency.setValueAtTime(
          melodyNotes[noteIndex],
          this.audioContext!.currentTime,
        )

        this.bassOscillator.frequency.setValueAtTime(
          bassNotes[bassIndex],
          this.audioContext!.currentTime,
        )

        this.currentBeat++
      },
      (60 / this.tempo) * 1000,
    )
  }

  updateIntensity(intensity: number) {
    if (this.musicGain) {
      this.musicGain.gain.linearRampToValueAtTime(
        0.15 * intensity,
        this.audioContext!.currentTime + 0.1,
      )
    }
    if (this.bassGain) {
      this.bassGain.gain.linearRampToValueAtTime(
        0.1 * intensity,
        this.audioContext!.currentTime + 0.1,
      )
    }
  }

  stopMusic() {
    if (this.beatInterval) {
      clearInterval(this.beatInterval)
    }
    if (this.musicOscillator) {
      this.musicOscillator.stop()
      this.musicOscillator = null
    }
    if (this.bassOscillator) {
      this.bassOscillator.stop()
      this.bassOscillator = null
    }
    this.isPlaying = false
  }

  playSound(
    frequency: number,
    duration: number,
    type: OscillatorType = 'sine',
  ) {
    if (!this.audioContext) return

    const osc = this.audioContext.createOscillator()
    const gain = this.audioContext.createGain()

    osc.connect(gain)
    gain.connect(this.masterGain!)

    osc.type = type
    osc.frequency.value = frequency
    gain.gain.value = 0.2

    osc.start()
    gain.gain.exponentialRampToValueAtTime(
      0.01,
      this.audioContext.currentTime + duration,
    )
    osc.stop(this.audioContext.currentTime + duration)
  }

  playJump() {
    this.playSound(400, 0.1, 'square')
  }

  playAttack() {
    this.playSound(200, 0.15, 'sawtooth')
  }

  playHit() {
    this.playSound(150, 0.2, 'square')
  }

  playCombo() {
    this.playSound(600, 0.1, 'sine')
  }
}
